import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Typography, Grid, Paper, List, ListItem, ListItemText, Alert } from '@mui/material';
import { Folder, Code, Storage, Update, Group, Forum } from '@mui/icons-material';
import './CSS/Dashboard.css';
import { API_URL } from '../apiConfig';

const Dashboard = () => {
  const [data, setData] = useState({
    projects: [], models: [], datasets: [], teamMembers: [], threads: [],
    activities: [], firstName: '', isAdmin: false, loading: true, error: ''
  });

  const cardData = [
    { title: "Total Projects", value: data.projects.length, icon: <Folder className="text-blue-500" /> },
    { title: "AI Models", value: data.models.length, icon: <Code className="text-green-500" /> },
    { title: "Datasets", value: data.datasets.length, icon: <Storage className="text-purple-500" /> }
  ];

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('accessToken');
      try {
        const userRes = await axios.get(`${API_URL}/api/user_details`, { headers: { Authorization: `Bearer ${token}` } });
        const userDetails = userRes.data;
        const isAdmin = userDetails.role === 'Admin';
        const userType = userDetails.type;

        const endpoints = ['projects', 'models', 'datasets', 'team_members', 'threads'];
        const responses = await Promise.all(
          endpoints.map(endpoint => axios.get(`${API_URL}/api/${endpoint}`, { headers: { Authorization: `Bearer ${token}` } }))
        );

        let activityRes = { data: [] };
        if (userType === 'user' || isAdmin) {
          activityRes = await axios.get(`${API_URL}/api/recent_activities`, { headers: { Authorization: `Bearer ${token}` } });
        }

        setData({
          projects: responses[0].data,
          models: responses[1].data,
          datasets: responses[2].data,
          teamMembers: responses[3].data,
          threads: responses[4].data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
          activities: activityRes.data.slice(0, 6),
          firstName: userDetails.contact_person.split(' ')[0],
          isAdmin,
          loading: false,
          error: ''
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        setData(prev => ({
          ...prev,
          loading: false,
          error: error.response?.status === 403
            ? 'You need to verify your email to access this page. Go to the settings page to resolve this.'
            : 'Failed to fetch data'
        }));
      }
    };

    fetchData();
  }, []);

  const formatActivityTimestamp = (timestamp) => {
    const now = moment();
    const activityTime = moment(timestamp);
    return now.diff(activityTime, 'days') < 7 ? activityTime.fromNow() : activityTime.format('DD MMM, YYYY');
  };

  if (data.loading) return <Typography variant="h6" className="text-gray-600 animate-pulse pt-16">Loading...</Typography>;
  if (data.error) return <Alert severity="error" className="bg-red-50 border-l-4 border-red-500 p-4 mt-16">{data.error}</Alert>;

  return (
    <div className="p-6 pt-16 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="dashboard-header mb-6">
        <div className="flex items-center mb-3">
          <div>
            <Typography variant="h4" className="text-3xl text-gray-800">{`Welcome back, ${data.firstName}`}</Typography>
            <Typography variant="subtitle1" className="text-lg text-gray-600 mt-1">{moment().format('dddd, DD MMMM')}</Typography>
          </div>
        </div>
      </div>

      <Grid container spacing={3}>
        {cardData.map((item, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Paper className="dashboard-card hover:shadow-lg transition-shadow duration-300 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="h6" className="text-lg text-gray-700">{item.title}</Typography>
                  <Typography variant="h3" color="primary" className="text-4xl mt-2">{item.value}</Typography>
                </div>
                <div className="text-4xl">{item.icon}</div>
              </div>
            </Paper>
          </Grid>
        ))}
      </Grid>
      
      <Grid container spacing={3} sx={{ mt: 3 }}>
        {[
          { title: 'Recent Activity', icon: <Update className="mr-2 text-blue-500" />, data: data.activities, accessor: 'description', secondary: activity => `${activity.type} - ${formatActivityTimestamp(activity.timestamp)}` },
          { title: 'Team Members', icon: <Group className="mr-2 text-green-500" />, data: data.teamMembers, accessor: 'name', secondary: member => member.role },
          { title: 'Recent Threads', icon: <Forum className="mr-2 text-purple-500" />, data: data.threads, accessor: 'subject', secondary: thread => `Created on: ${moment(thread.created_at).format('DD MMM, YYYY')}` }
        ].map((section, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Paper sx={{ p: 3, height: '400px', overflow: 'hidden' }} className="hover:shadow-lg transition-shadow duration-300">
              <Typography variant="h6" className="text-xl text-gray-800 pb-2 border-b border-gray-200 flex items-center sticky top-0 bg-white z-10">
                {section.icon}
                {section.title}
              </Typography>
              <List className="overflow-y-auto h-[calc(100%-48px)]">
                {section.data.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" className="text-gray-500 italic">
                    {section.title === 'Recent Activity' && !data.isAdmin ? 'You are not authorized to see this content.' : `No ${section.title.toLowerCase()}.`}
                  </Typography>
                ) : (
                  section.data.map((item, idx) => (
                    <ListItem key={idx} className="hover:bg-gray-50 rounded transition-colors duration-150">
                      <ListItemText 
                        primary={item[section.accessor].length > 30 ? `${item[section.accessor].substring(0, 30)}...` : item[section.accessor]}
                        secondary={section.secondary(item)}
                        primaryTypographyProps={{ noWrap: true, className: "text-gray-700" }}
                        secondaryTypographyProps={{ noWrap: true, className: "text-gray-500" }}
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default Dashboard;